import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Loader2, Mail, RefreshCw, ShieldAlert, ShieldCheck } from 'lucide-react';
import { SEO } from '@/components/SEO';

interface LogRow {
  id: string;
  created_at: string;
  template_name: string;
  recipient_email: string;
  status: string;
  error_message: string | null;
}

const STORAGE_KEY = 'aureum_email_test_inbox';

const statusColor = (s: string) => {
  switch (s) {
    case 'sent': return 'bg-green-600';
    case 'pending': return 'bg-amber-500';
    case 'failed':
    case 'dlq':
    case 'bounced': return 'bg-red-600';
    case 'suppressed': return 'bg-zinc-500';
    default: return 'bg-zinc-400';
  }
};

const EmailTest = () => {
  const { user, loading } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const [inbox, setInbox] = useState('');
  const [running, setRunning] = useState(false);
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate('/auth');
  }, [user, loading, navigate]);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) setInbox(saved);
    else if (user?.email) setInbox(user.email);
  }, [user]);

  const fetchLogs = async (email?: string) => {
    setRefreshing(true);
    const target = email ?? inbox;
    let q = supabase
      .from('email_send_log')
      .select('id, created_at, template_name, recipient_email, status, error_message')
      .order('created_at', { ascending: false })
      .limit(20);
    if (target) q = q.eq('recipient_email', target);
    const { data, error } = await q;
    if (error) toast.error('Could not load delivery log: ' + error.message);
    else setLogs((data as LogRow[]) || []);
    setRefreshing(false);
  };

  useEffect(() => { if (user) fetchLogs(); /* eslint-disable-next-line */ }, [user]);

  const runTest = async () => {
    if (!inbox || !inbox.includes('@')) {
      toast.error('Enter a valid test inbox address');
      return;
    }
    localStorage.setItem(STORAGE_KEY, inbox);
    setRunning(true);
    toast.info('Triggering test signup...');

    // Use a + alias so the same inbox can run repeated tests without colliding
    const ts = Date.now();
    const [local, domain] = inbox.split('@');
    const aliasEmail = `${local}+test-${ts}@${domain}`;
    const password = `Aur3um!VII-${ts}-${Math.random().toString(36).slice(2, 8)}Q`;

    const { error } = await supabase.auth.signUp({
      email: aliasEmail,
      password,
      options: { emailRedirectTo: `${window.location.origin}/` },
    });

    if (error) {
      toast.error('Signup failed: ' + error.message);
      setRunning(false);
      return;
    }

    toast.success(`Verification email enqueued for ${aliasEmail}`);

    // Poll for status updates
    for (let i = 0; i < 8; i++) {
      await new Promise((r) => setTimeout(r, 2500));
      const { data } = await supabase
        .from('email_send_log')
        .select('id, created_at, template_name, recipient_email, status, error_message')
        .eq('recipient_email', aliasEmail)
        .order('created_at', { ascending: false });
      if (data && data.length) {
        setLogs((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const merged = [...(data as LogRow[]).filter((d) => !existingIds.has(d.id)), ...prev];
          return merged.slice(0, 20);
        });
        const latest = data[0];
        if (latest.status === 'sent') {
          toast.success('Email sent successfully — check the inbox.');
          break;
        }
        if (['failed', 'dlq', 'bounced'].includes(latest.status)) {
          toast.error(`Delivery failed: ${latest.error_message ?? latest.status}`);
          break;
        }
      }
    }
    setRunning(false);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <>
      <SEO title="Email Test | Aureum VII" description="Trigger and verify branded auth emails." type="website" />
      <div className="min-h-screen bg-background py-12 px-4">
        <div className="max-w-4xl mx-auto space-y-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Email Delivery Test</h1>
            <p className="text-muted-foreground mt-1">
              One-click test of the branded verification email and live delivery status.
            </p>
          </div>

          {!roleLoading && (
            isAdmin() ? (
              <Alert className="border-green-600/40 bg-green-600/5">
                <ShieldCheck className="h-4 w-4 text-green-600" />
                <AlertTitle>Admin access confirmed</AlertTitle>
                <AlertDescription className="text-muted-foreground">
                  Signed in as <code>{user?.email}</code> with the <code>admin</code> role — the delivery log below shows live data.
                </AlertDescription>
              </Alert>
            ) : (
              <Alert variant="destructive">
                <ShieldAlert className="h-4 w-4" />
                <AlertTitle>Admin role required to see delivery status</AlertTitle>
                <AlertDescription className="space-y-2">
                  <p>
                    You can still trigger test signups, but the <strong>Delivery status</strong> table will stay empty
                    until your account (<code>{user?.email}</code>) has the <code>admin</code> role.
                  </p>
                  <p className="font-medium">Grant admin access in one of two ways:</p>
                  <ol className="list-decimal pl-5 space-y-1 text-sm">
                    <li>
                      Ask an existing admin to open the{' '}
                      <a
                        href="/en/admin?tab=agents"
                        className="font-semibold text-primary underline underline-offset-2 hover:opacity-80"
                      >
                        Agent Management panel
                      </a>
                      , find your email, and assign the <strong>Admin</strong> role.
                    </li>
                    <li>
                      Or, if you have backend access, run this SQL in Lovable Cloud:
                      <pre className="mt-1 rounded bg-muted p-2 text-xs overflow-x-auto">{`INSERT INTO public.user_roles (user_id, role)
SELECT id, 'admin' FROM auth.users WHERE email = '${user?.email ?? 'you@example.com'}'
ON CONFLICT (user_id, role) DO NOTHING;`}</pre>
                    </li>
                  </ol>
                  <p className="text-xs">After granting the role, sign out and back in, then refresh this page.</p>
                </AlertDescription>
              </Alert>
            )
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> Test inbox</CardTitle>
              <CardDescription>
                Each test uses a unique <code>+test-…</code> alias so the same inbox can be reused.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="inbox">Email address</Label>
                <Input
                  id="inbox"
                  type="email"
                  value={inbox}
                  onChange={(e) => setInbox(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={runTest} disabled={running} className="flex-1">
                  {running ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</> : 'Send test verification email'}
                </Button>
                <Button variant="outline" onClick={() => fetchLogs()} disabled={refreshing}>
                  <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Delivery status</CardTitle>
              <CardDescription>Latest 20 entries from the email send log.</CardDescription>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <p className="text-sm text-muted-foreground">No emails logged yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Template</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="whitespace-nowrap text-xs">
                          {new Date(row.created_at).toLocaleTimeString()}
                        </TableCell>
                        <TableCell>{row.template_name}</TableCell>
                        <TableCell className="text-xs max-w-[220px] truncate">{row.recipient_email}</TableCell>
                        <TableCell>
                          <Badge className={`${statusColor(row.status)} text-white`}>{row.status}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-destructive max-w-[180px] truncate">
                          {row.error_message || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default EmailTest;
